import { firebaseConfig as FIREBASE_DEVELOPMENT } from 'lib/config/firebase/dev'
import { firebaseConfig as FIREBASE_PRODUCTION } from 'lib/config/firebase/prod'

import { isFlutterApp } from './appBridge'
import { SHOPIFY_APP_CLIENT_IDS } from './shopifyEmbedded'

const getEnv = () => {
  try {
    if (
      ['localhost', 'adhil.ngrok.dev', 'ajith.ngrok.dev']?.includes(
        window.location.hostname
      )
    ) {
      return 'local'
    }
    return import.meta.env.MODE || 'development'
  } catch (e) {
    return 'development'
  }
}

const getMode = () => {
  try {
    return process?.env?.MODE || ''
  } catch (e) {
    return ''
  }
}

const ENV = getEnv()
const MODE = getMode()

// The one Meta app every environment runs embedded signup against. The
// staging app ('404784014777310') and its WABA-only config
// ('971022575034531') were dropped on 2026-09-10: that app is broken, so dev
// and staging share the prod pair rather than a pair that cannot complete a
// signup. FB.login rejects a config_id from a different app than FB.init ran
// with, so app id and config id must always move together.
const PROD_FB_ID = '4504112716382182'

// The WABA-sharing embedded-signup configuration, Marketing Messages API
// disabled on it. Used by every environment.
//
// This is what makes the ZC-3100 flow WABA-only: the pipeline sends
// featureType 'only_waba_sharing' (see FEATURE_TYPE_ONLY_WABA_SHARING), and
// Meta honours it only when the configuration behind config_id is itself a
// WABA-sharing one. On the earlier onboarding configs ('548992707333610', and
// the MM-Lite '2253135465475515' rolled back on 2026-08-17) Meta ran its own
// phone-number step instead, duplicating Zoko's /phone-setup.
const META_CONFIG_ID = '1085052220683898'

// ZC-3324: the pre-pipeline embedded-signup configuration (the prod config
// until 2026-09-08). Unlike META_CONFIG_ID it is not WABA-sharing, so Meta
// runs its own phone-number step inside the dialog — which is exactly what a
// merchant migrating a number from another provider needs: add-phone-number
// refuses such a number (136021) while it still lives on the other WABA, and
// Meta's own step moves it across with its blue tick intact. Same Facebook
// app as META_CONFIG_ID, so FB.login accepts either under one FB.init.
const META_MIGRATION_CONFIG_ID = '548992707333610'

// Embedded Signup v4 (Meta's cut-over is 8 October 2026). Created in the app
// dashboard under Facebook Login for Business with the "WhatsApp Embedded
// Signup" login variation — configurations made before v4 cannot be reused,
// so these are never the ids above. Nothing in the dashboard names a version:
// what makes a launch v4 is the shape FB.login sends against this id.
//
// Kept beside the v2 ids rather than replacing them so a rollback is one flag
// (EMBEDDED_SIGNUP_V4_ENABLED). A blank id here keeps that launch on v2:
// pairing a v4 shape with a v2 configuration is the one combination Meta
// refuses outright, so a half-finished dashboard setup must not reach it.
const META_V4_CONFIG_ID = '1509994234267926'
// Migrating merchants launch v4 against the same configuration as a standard
// signup (2026-09-30): the v2 migration fallback sent the legacy v2 shape,
// which Meta stops honouring at the v4 cut-over. Blank this to fall back to
// META_MIGRATION_CONFIG_ID on v2 again.
const META_V4_MIGRATION_CONFIG_ID = META_V4_CONFIG_ID

// Click-redirector bases still present on approved Meta templates, across
// every environment. New templates are written with DYNAMIC_LINK_API; these
// must stay readable until the ZC-3142 template backfill has rewritten them.
// Remove entries after that.
const LEGACY_DYNAMIC_LINK_APIS = [
  'https://web3.api.zoko.io/v3/click?to=',
  'https://meta-redirect.link/v3/click?to=',
  'https://web.gke.zoko.dev/v3/click?to=',
  'https://web-api-3.zoko.dev/v3/click?to=',
  'https://web-api-3.wickes.zoko.io/v3/click?to=',
]

const development = {
  ENV: ENV,
  BASE_API: 'https://web.gke.zoko.dev/v3',
  BASE_API_V4: 'https://web.gke.zoko.dev/v4',
  // Unauthenticated v3 health host (serves /ping). Differs from BASE_API host in dev.
  V3_PING_HOST: 'https://web-api-3.zoko.dev',
  // Firebase Auth backend (Google Identity Toolkit) — the server our custom-token
  // logins depend on. Google-global, so the same across every env.
  FIREBASE_AUTH_HOST: 'https://identitytoolkit.googleapis.com',
  // Firebase token-refresh backend. If blocked, login succeeds but every API call
  // silently loses its auth header ~1h later. Google-global.
  FIREBASE_SECURETOKEN_HOST: 'https://securetoken.googleapis.com',
  // Firestore backend — live config, banners, forced-refresh, blocked-user switch.
  // Google-global.
  FIREBASE_FIRESTORE_HOST: 'https://firestore.googleapis.com',
  // Google reCAPTCHA — required for phone-OTP login and 2FA/MFA. Google-global.
  RECAPTCHA_HOST: 'https://www.google.com/recaptcha/api.js',
  // reCAPTCHA Enterprise — guards store creation with a visible "I'm not a
  // robot" checkbox on /create-account. Google-global host; the site key is per
  // GCP project, so every env that is not zoko-web2-test must override
  // RECAPTCHA_SITE_KEY or it presents a key its domain is not registered
  // against.
  //
  // This must stay a CHECKBOX key. lib/recaptcha renders a widget and reads the
  // token from its callback; an invisible/score key would render nothing and
  // no token would ever arrive. The API must be configured with the same key —
  // CreateAssessment names the expected site key, so a mismatch fails every
  // assessment.
  RECAPTCHA_ENTERPRISE_HOST: 'https://www.google.com/recaptcha/enterprise.js',
  RECAPTCHA_SITE_KEY: '6LephmwtAAAAAGKFE7UH1YuoU8HlJCoc3-vMQQvi',
  SEND_API_V2: 'https://send-api.zoko.dev/v2',
  BASE_FLOW_API: 'https://zcom.zoko.dev',
  WEBHOOK_BASE_URL: 'https://webhooks.flowhippo.dev/event/webhook',
  // MQTT_URL: 'wss://mqtt.gke.zoko.dev/mqtt',
  MQTT_URL: 'wss://updates.zoko.dev/mqtt',
  FIREBASE: FIREBASE_DEVELOPMENT,
  UTM_SOURCE: 'zoko',
  UTM_MEDIUM: 'web_app',
  UTM_CAMPAIGN_WA: 'whatsapp',
  WA_URL_SHORTENER_API: 'https://test.gus.zoko.dev/api/v1',
  PAY_URL_SHORTENER_API: 'https://test.gus.zoko.dev/api/v1',
  URL_SHORTENER_API_2FA_SECRET_KEY: 'P3JTVCHYQ3F55QKC',
  BUGSNAG_API_KEY: '9998a9e9f8868f6565f05162e0066dc0',
  STRIPE_API_KEY:
    'pk_test_51I9plQAeE7attOCzbDscWtPULZspmbnKrKV5eCrDdHQfjtJgyQcNjavZJYDqTyArC5nCQwOBoh7fFBzEE2akjF7600HgeDaHgI',
  SYNC_API_URL: 'https://test.management.zoko.dev',
  USER_PILOT: 'NX-60eg28i2',
  SHOPIFY_API_KEY: SHOPIFY_APP_CLIENT_IDS.development,
  FACEBOOK_APP_ID: PROD_FB_ID,
  FACEBOOK_BASE_URL: 'https://graph.facebook.com/v23.0',
  FACEBOOK_SCOPES:
    'email,public_profile,pages_show_list,pages_messaging,business_management',
  INSTAGRAM_SCOPES:
    'public_profile,email,instagram_manage_messages,pages_manage_metadata,pages_messaging,instagram_basic,pages_read_engagement,pages_show_list,business_management,instagram_manage_comments,ads_read,instagram_manage_insights',
  ADSMANAGER_SCOPES: `ads_read,pages_read_engagement`,
  FACEBOOK_PAGE_FIELDS:
    'picture{url},id,name,access_token,username,instagram_business_account{id,username,profile_picture_url,name}',
  ADSMANAGER_FIELDS: 'id,name,account_id',
  WHATSAPP_EXPIRY_HOURS: 24,
  MESSENGER_EXPIRY_HOURS: 168, //7 days
  DYNAMIC_LINK_API: 'https://web.gke.zoko.dev/v3/click?to=',
  WIDGET_SCRIPT_URL:
    'https://atlantis.zoko.dev/scripts/main.js?v=' + Date.now(),
  // Walkthrough for the Shopify app embed section. One copy on the live CDN
  // serves every environment.
  APP_EMBED_ACTIVATION_VIDEO_URL:
    'https://cdn.live.zoko.io/store/customers/a3994ad5-731b-4270-87ae-87e0593134d3-zo-ko-zoko-app-embed-activation-v4.mp4',
  FLOWHIPPO_URL: '/zoko_flows',
  HEAVY_UTILS_URL: 'https://heavy-utils.zoko.dev',
  DIALOGUE_360_PARTNER_ID: 'QKlWYqPA',
  DIALOGUE_360_PARTNER_ID_INDIA: 'uAGAe8PA',
  DOPT_BLOCKS_API:
    'blocks-96096f76046f6b7946cdbbf244d2fbd421e6bfd85df59c9ce627b8be6e31f59c_MTI1MQ==',
  DOPT_USER_API_KEY:
    'users-df0f297d93179481360c54a777f8f0e705070076cee84c37d48023725631c834_MjY0Ng==',
  CONNECT_META_CONFIG_ID: META_CONFIG_ID,
  CONNECT_META_MIGRATION_CONFIG_ID: META_MIGRATION_CONFIG_ID,
  CONNECT_META_V4_CONFIG_ID: META_V4_CONFIG_ID,
  CONNECT_META_V4_MIGRATION_CONFIG_ID: META_V4_MIGRATION_CONFIG_ID,
  GOOGLE_API_KEY: 'AIzaSyCJmFPpE8ZzeUbzt9xfez-L0imvnv2QDSM',
  GOOGLE_APP_ID: '571820199807',
  GOOGLE_CLIENT_ID:
    '571820199807-tij2d5dokflb5obrcuvk2gppm9lg2i2c.apps.googleusercontent.com',
  GOOGLE_SCOPES: [
    'https://www.googleapis.com/auth/userinfo.profile',
    'https://www.googleapis.com/auth/userinfo.email',
    'https://www.googleapis.com/auth/drive.file',
  ],
  OLD_VERSION: 'https://app.dev.zoko.io/',
  POSTHOG_API_KEY: 'phc_sHe9vi6sJmguFMVFBCF8j3iHw8tdzhKDNXJs3es3WecU',
  POSTHOG_HOST: 'https://t.zoko.io',
  // Google Analytics 4 (gtag.js). Google-global script host.
  GA_SCRIPT_HOST: 'https://www.googletagmanager.com/gtag/js',
  // Empty measurement id = GA disabled. Every non-production env inherits this
  // blank, so gtag never loads and no event leaves the browser outside live —
  // the id itself is the env gate, so there is no separate GA on/off flag.
  GA_MEASUREMENT_ID: '',
  SHOPIFY_QR_TRACKING_URL:
    'https://us-central1-zoko-web2-test.cloudfunctions.net/shopifyRedirection',
  BUZZER_API: 'https://us-central1-zoko-web2-test.cloudfunctions.net/setBuzzer',
  SHOPIFY_APP_STORE_URL: 'https://apps.shopify.com/whatsapp-button-chat',
  ZAPIER_SHOPIFY_LEAD_HOOK: '',
  SHOPIFY_SUPPORT_WHATSAPP_URL: 'https://wa.me/919586538447',
  // Zoko support number (bare digits, no wa.me prefix) the onboarding
  // connection-pipeline's support pill messages — same number already used by
  // SHOPIFY_SUPPORT_WHATSAPP_URL above, reused here because the wa.me link
  // needs to carry a per-screen prefilled text (buildSupportPrefill) rather
  // than the fixed URL.
  SUPPORT_WHATSAPP_NUMBER: '919586538447',
  // Self-serve fixes offered when a business number cannot be activated yet:
  // the number is still bound to the WhatsApp app, or still held by the
  // previous API provider behind 2FA. Linked from the onboarding screen's
  // business-phone error modal.
  ONBOARDING_DELETE_WHATSAPP_GUIDE_URL:
    'https://www.zoko.io/learning-micro-lessons/delete-your-whatsapp',
  ONBOARDING_DISABLE_2FA_GUIDE_URL:
    'https://www.zoko.io/learning-micro-lessons/disable-2fa-for-whatsapp-api',
  // Meta's own rules for the WhatsApp display name. Rejections here cost the
  // merchant a review cycle, so the rules are linked from the field itself.
  ONBOARDING_DISPLAY_NAME_GUIDELINES_URL:
    'https://www.facebook.com/business/help/757569725593362',
  // "Preparing your number for WhatsApp API activation" — the guide bullet
  // under the business WhatsApp number on /phone-setup (spec 2026-09-17;
  // dedicated article supplied by Piyush on ZC-3269, 2026-09-22).
  ONBOARDING_NUMBER_PREP_GUIDE_URL:
    'https://www.zoko.io/learning-article/whatsapp-api-activation',
  SHOPIFY_WEBVIEW_URL: 'https://shopify-webview.zoko.dev',
  // Cache-first webview boot: reuse the last known entry script URL from
  // localStorage while /api/manifest revalidates in the background. Set to
  // false to always wait for the manifest (e.g. while debugging deploys).
  SHOPIFY_WEBVIEW_ENTRY_CACHE: true,
}

const staging = {
  ...development,
  // FACEBOOK_APP_ID / CONNECT_META_CONFIG_ID intentionally inherited from
  // development (the prod pair) — see the PROD_FB_ID note on why the staging
  // Meta app was dropped.
  BASE_API: 'https://stage.web.api.zoko.dev/v3',
  MQTT_URL: 'wss://mqtt.zoko.io/mqtt',
  FIREBASE: FIREBASE_PRODUCTION,
  URL_SHORTENER_API: 'https://wa.zoko.io/api/v1',
  PAY_URL_SHORTENER_API: 'https://pay.zoko.io/api/v1',
  URL_SHORTENER_API_2FA_SECRET_KEY: '6OMTZVRDWSRTB6KL',
  USER_PILOT: 'NX-60eg28i2',
  WHATSAPP_EXPIRY_HOURS: 24, // 1 day
  MESSENGER_EXPIRY_HOURS: 168, // 7 days
}

const production = {
  ...development,
  BASE_API: 'https://web3.api.zoko.io/v3',
  BASE_API_V4: 'https://web-api.zoko.io/v4',
  V3_PING_HOST: 'https://web3.api.zoko.io',
  // Live checkbox key, GCP project zoko-web-prod. Overrides development's key
  // (GCP project zoko-web2-test) — without this override the dev-project key
  // would reach live, whose domain is not registered against it, so the widget
  // would refuse to render and no merchant could get a token. staging_prod and
  // wicks spread production, so they inherit this key too.
  RECAPTCHA_SITE_KEY: '6LcZ1GotAAAAAEKtWCIY1Yt81WK3Mt0GHza4SNyi',
  BASE_FLOW_API: 'https://api.chatpowers.com',
  WEBHOOK_BASE_URL: 'https://hooks.chatpowers.com/event/webhook',
  MQTT_URL: 'wss://inbox.live.zoko.io/mqtt',
  FIREBASE: FIREBASE_PRODUCTION,
  URL_SHORTENER_API: 'https://wa.zoko.io/api/v1',
  PAY_URL_SHORTENER_API: 'https://pay.zoko.io/api/v1',
  URL_SHORTENER_API_2FA_SECRET_KEY: '6OMTZVRDWSRTB6KL',
  STRIPE_API_KEY:
    'pk_live_51I9plQAeE7attOCzo50v5aBqzkvJjyl4l67OxOPybp6oQzWeDXj3eSdfWyp55XPY5M1WvflyshVqowh2QSedEqnt00yyYtIDxo',
  SYNC_API_URL: 'https://management.zoko.io',
  USER_PILOT: 'NX-60eg28i2',
  SHOPIFY_API_KEY: SHOPIFY_APP_CLIENT_IDS.production,
  FACEBOOK_APP_ID: PROD_FB_ID,
  WHATSAPP_EXPIRY_HOURS: 24, // 1 day
  MESSENGER_EXPIRY_HOURS: 168, // 7 days,
  DYNAMIC_LINK_API: 'https://meta-redirect.link/v3/click?to=',
  WIDGET_SCRIPT_URL: 'https://atlantis.live.zoko.io/scripts/main.js',
  FLOWHIPPO_URL: 'https://app.flowhippo.com',
  DIALOGUE_360_PARTNER_ID: 'QKlWYqPA',
  DOPT_BLOCKS_API:
    'blocks-7af3e1c35d9606f635f068157eecd787e8c2fed3cda43ac32d6efad71e246c08_MjY2Mg==',
  DOPT_USER_API_KEY:
    'users-99a2d6619c891c40d4f7e561da56c0746513c80057477b6e255c7457fca3907a_MjY2Mw==',
  CONNECT_META_CONFIG_ID: META_CONFIG_ID,
  CONNECT_META_MIGRATION_CONFIG_ID: META_MIGRATION_CONFIG_ID,
  CONNECT_META_V4_CONFIG_ID: META_V4_CONFIG_ID,
  CONNECT_META_V4_MIGRATION_CONFIG_ID: META_V4_MIGRATION_CONFIG_ID,
  GOOGLE_API_KEY: 'AIzaSyAs3ypSaVi-k-zOGvvQ4RhGtb0txRgKPnc',
  GOOGLE_APP_ID: '153839500012',
  GOOGLE_CLIENT_ID:
    '153839500012-l10gdeer7vt2e3kn7u1qmoo9fnrlcp58.apps.googleusercontent.com',
  OLD_VERSION: 'https://app.live.zoko.io/',
  POSTHOG_API_KEY: 'phc_qYEd6tZDxQyB2NXaPsH9qNbm0MBXhtgI5qlrjTWjL1b',
  POSTHOG_HOST: 'https://t.zoko.io',
  // Live GA4 property. staging_prod and wicks spread production, so they report
  // into this same property.
  GA_MEASUREMENT_ID: 'G-90XJ594G83',
  SHOPIFY_QR_TRACKING_URL:
    'https://us-central1-zoko-web-prod.cloudfunctions.net/shopifyRedirection',
  ZAPIER_SHOPIFY_LEAD_HOOK:
    'https://hooks.zapier.com/hooks/catch/8300538/4ydu868/',
  SHOPIFY_SUPPORT_WHATSAPP_URL: 'https://wa.me/919586538447',
  SUPPORT_WHATSAPP_NUMBER: '919586538447',
  BUZZER_API: 'https://us-central1-zoko-web-prod.cloudfunctions.net/setBuzzer',
  SHOPIFY_WEBVIEW_URL: 'https://powered-by.zoko.io',
}

const local = {
  ...development,
  // BASE_API: 'https://abhai.in.ngrok.io/v3',
  // MQTT_URL: 'wss://updates.zoko.dev/mqtt',
  // BASE_API: 'http://localhost:8080/v3',
  // BASE_API: 'https://sachin.zoko.ngrok.io/v3',
  // BASE_API_V4: 'http://localhost:8080/v4',
  // BASE_API: 'https://rabi-webapiv3.ngrok.io/v3',
  // BASE_API: 'https://11b45a30081b.ngrok.app/v3',
  // BASE_API: 'https://zokohook.ngrok.io/v3',
  // BASE_API: 'https://sachin.zoko.ngrok.io/v3',
  // BASE_FLOW_API: 'https://ac33f43ab41d.ngrok.app',
  OLD_VERSION: window.location.origin,
  // OLD_VERSION: 'https://app.live.zoko.io/',
  // WIDGET_SCRIPT_URL: 'http://localhost:3030/scripts/main.js?v=' + Date.now(), // atlantis server must be running
  SHOPIFY_WEBVIEW_URL: 'http://localhost:3010', // shopify-webview server must be running
}

const staging_prod = {
  ...production,
  MQTT_URL: 'wss://inbox.live.zoko.io/mqtt',
  OLD_VERSION: 'https://app.live.zoko.io/',
}

const wicks = {
  ...production,
  BASE_API: 'https://web-api-3.wickes.zoko.io/v3',
  BASE_API_V4: 'https://web-api-4.wickes.zoko.io/v4',
  V3_PING_HOST: 'https://web-api-3.wickes.zoko.io',
  BASE_FLOW_API: 'https://flows-api.wickes.zoko.io',
  WEBHOOK_BASE_URL: 'https://flows-webhook.wickes.zoko.io/event/webhook',
  MQTT_URL: 'wss://mqtt.wickes.zoko.io/mqtt',
  URL_SHORTENER_API: 'https://wa.wickes.zoko.io/api/v1',
  PAY_URL_SHORTENER_API: 'https://pay.wickes.zoko.io/api/v1',
  FACEBOOK_APP_ID: '4504112716382182',
  DYNAMIC_LINK_API: 'https://web-api-3.wickes.zoko.io/v3/click?to=',
  FLOWHIPPO_URL: 'https://app.flowhippo.com',
  HEAVY_UTILS_URL: 'https://heavy-utils.zoko.dev',
  CONNECT_META_CONFIG_ID: META_CONFIG_ID,
  CONNECT_META_MIGRATION_CONFIG_ID: META_MIGRATION_CONFIG_ID,
  CONNECT_META_V4_CONFIG_ID: META_V4_CONFIG_ID,
  CONNECT_META_V4_MIGRATION_CONFIG_ID: META_V4_MIGRATION_CONFIG_ID,
  OLD_VERSION: 'https://app.wickes.zoko.io/',
  // wicks spreads `production`, inheriting the zoko-web-prod Enterprise key —
  // but app.wickes.zoko.io is not a registered domain on that key. Worse,
  // wicks overrides OLD_VERSION to its own host above, so IS_DEFAULT_HOST is
  // TRUE here (it never falls into the white-label skip). Without this
  // override, execute() would throw "Invalid domain for site key" and block
  // every org creation on this tenant. Blank key = isRecaptchaConfigured()
  // reads false = guard stays inert.
  RECAPTCHA_SITE_KEY: '',
}

const configs = {
  development,
  staging,
  production,
  local,
  staging_prod,
  wicks,
}

const currentConfig = configs[ENV]

const config = {
  ENV: ENV,
  BASE_API: currentConfig.BASE_API,
  MQTT_URL: currentConfig.MQTT_URL,
  FIREBASE: currentConfig.FIREBASE,
  UTM_SOURCE: currentConfig.UTM_SOURCE,
  UTM_MEDIUM: currentConfig.UTM_MEDIUM,
  UTM_CAMPAIGN_WA: currentConfig.UTM_CAMPAIGN_WA,
  WA_URL_SHORTENER_API: currentConfig.WA_URL_SHORTENER_API,
  PAY_URL_SHORTENER_API: currentConfig.PAY_URL_SHORTENER_API,
  URL_SHORTENER_API_2FA_SECRET_KEY:
    currentConfig.URL_SHORTENER_API_2FA_SECRET_KEY,
  BUGSNAG_API_KEY: currentConfig.BUGSNAG_API_KEY,
  STRIPE_API_KEY: currentConfig.STRIPE_API_KEY,
  SYNC_API_URL: currentConfig.SYNC_API_URL,
  USER_PILOT: currentConfig.USER_PILOT,
  SHOPIFY_API_KEY: currentConfig.SHOPIFY_API_KEY,
  FACEBOOK_APP_ID: currentConfig.FACEBOOK_APP_ID,
  FACEBOOK_BASE_URL: currentConfig.FACEBOOK_BASE_URL,
  FACEBOOK_SCOPES: currentConfig.FACEBOOK_SCOPES,
  INSTAGRAM_SCOPES: currentConfig.INSTAGRAM_SCOPES,
  FACEBOOK_PAGE_FIELDS: currentConfig.FACEBOOK_PAGE_FIELDS,
  WHATSAPP_EXPIRY_HOURS: currentConfig.WHATSAPP_EXPIRY_HOURS,
  MESSENGER_EXPIRY_HOURS: currentConfig.MESSENGER_EXPIRY_HOURS,
  DYNAMIC_LINK_API: currentConfig.DYNAMIC_LINK_API,
  LEGACY_DYNAMIC_LINK_APIS,
  WIDGET_SCRIPT_URL: currentConfig.WIDGET_SCRIPT_URL,
  APP_EMBED_ACTIVATION_VIDEO_URL: currentConfig.APP_EMBED_ACTIVATION_VIDEO_URL,
  ADSMANAGER_SCOPES: currentConfig.ADSMANAGER_SCOPES,
  ADSMANAGER_FIELDS: currentConfig.ADSMANAGER_FIELDS,
  FLOWHIPPO_URL: currentConfig.FLOWHIPPO_URL,
  BASE_API_V4: currentConfig.BASE_API_V4,
  V3_PING_HOST: currentConfig.V3_PING_HOST,
  FIREBASE_AUTH_HOST: currentConfig.FIREBASE_AUTH_HOST,
  FIREBASE_SECURETOKEN_HOST: currentConfig.FIREBASE_SECURETOKEN_HOST,
  FIREBASE_FIRESTORE_HOST: currentConfig.FIREBASE_FIRESTORE_HOST,
  RECAPTCHA_HOST: currentConfig.RECAPTCHA_HOST,
  // Per-env Firebase login/popup domain, derived from the single source of truth
  // (config.FIREBASE.authDomain) so it never drifts.
  FIREBASE_AUTH_DOMAIN_URL: 'https://' + currentConfig.FIREBASE.authDomain,
  RECAPTCHA_ENTERPRISE_HOST: currentConfig.RECAPTCHA_ENTERPRISE_HOST,
  RECAPTCHA_SITE_KEY: currentConfig.RECAPTCHA_SITE_KEY,
  HEAVY_UTILS_URL: currentConfig.HEAVY_UTILS_URL,
  DIALOGUE_360_PARTNER_ID: currentConfig.DIALOGUE_360_PARTNER_ID,
  DIALOGUE_360_PARTNER_ID_INDIA: currentConfig.DIALOGUE_360_PARTNER_ID_INDIA,
  IS_PROD: ENV === 'production' || ENV === 'staging_prod',
  IS_TEST_MODE: MODE === 'test',
  DOPT_BLOCKS_API: currentConfig.DOPT_BLOCKS_API,
  DOPT_USER_API_KEY: currentConfig.DOPT_USER_API_KEY,
  CONNECT_META_CONFIG_ID: currentConfig.CONNECT_META_CONFIG_ID,
  CONNECT_META_MIGRATION_CONFIG_ID:
    currentConfig.CONNECT_META_MIGRATION_CONFIG_ID,
  CONNECT_META_V4_CONFIG_ID: currentConfig.CONNECT_META_V4_CONFIG_ID || '',
  CONNECT_META_V4_MIGRATION_CONFIG_ID:
    currentConfig.CONNECT_META_V4_MIGRATION_CONFIG_ID || '',
  RESTRICT_ACCESS: currentConfig.RESTRICT_ACCESS,
  BASE_FLOW_API: currentConfig.BASE_FLOW_API,
  GOOGLE_CLIENT_ID: currentConfig.GOOGLE_CLIENT_ID,
  GOOGLE_SCOPES: currentConfig.GOOGLE_SCOPES,
  GOOGLE_API_KEY: currentConfig.GOOGLE_API_KEY,
  GOOGLE_APP_ID: currentConfig.GOOGLE_APP_ID,
  WEBHOOK_BASE_URL: currentConfig.WEBHOOK_BASE_URL,
  OLD_VERSION: currentConfig.OLD_VERSION,
  DEBUG_MODE: window?.localStorage?.debugMode === '1',
  // Single source of truth for the zustand devtools `enabled` flag. Opt-in on
  // every environment: without debugMode an explicit `enabled` with no Redux
  // devtools extension present makes zustand warn once per store import.
  DEVTOOLS_ENABLED: window?.localStorage?.debugMode === '1',
  IS_MOBILE_APP: isFlutterApp,
  POSTHOG_API_KEY: currentConfig.POSTHOG_API_KEY,
  POSTHOG_HOST: currentConfig.POSTHOG_HOST,
  // PostHog's own UI (toolbar, assets); events go through POSTHOG_HOST.
  POSTHOG_UI_HOST: 'https://us.posthog.com',
  GA_SCRIPT_HOST: currentConfig.GA_SCRIPT_HOST,
  GA_MEASUREMENT_ID: currentConfig.GA_MEASUREMENT_ID || '',
  SHOPIFY_QR_TRACKING_URL: currentConfig.SHOPIFY_QR_TRACKING_URL,
  IS_DEFAULT_HOST:
    window.location.host
      .replace(`http://`, '')
      .replace(`https://`, '')
      .replaceAll('/', '') ===
    currentConfig.OLD_VERSION.replace(`http://`, '')
      .replace(`https://`, '')
      .replaceAll('/', ''),
  SHOPIFY_APP_STORE_URL: currentConfig.SHOPIFY_APP_STORE_URL,
  ZAPIER_SHOPIFY_LEAD_HOOK: currentConfig.ZAPIER_SHOPIFY_LEAD_HOOK || '',
  SHOPIFY_SUPPORT_WHATSAPP_URL: currentConfig.SHOPIFY_SUPPORT_WHATSAPP_URL,
  SUPPORT_WHATSAPP_NUMBER: currentConfig.SUPPORT_WHATSAPP_NUMBER,
  ONBOARDING_DELETE_WHATSAPP_GUIDE_URL:
    currentConfig.ONBOARDING_DELETE_WHATSAPP_GUIDE_URL || '',
  ONBOARDING_DISABLE_2FA_GUIDE_URL:
    currentConfig.ONBOARDING_DISABLE_2FA_GUIDE_URL || '',
  ONBOARDING_DISPLAY_NAME_GUIDELINES_URL:
    currentConfig.ONBOARDING_DISPLAY_NAME_GUIDELINES_URL || '',
  ONBOARDING_NUMBER_PREP_GUIDE_URL:
    currentConfig.ONBOARDING_NUMBER_PREP_GUIDE_URL || '',
  SEND_API_V2: currentConfig.SEND_API_V2,
  BUZZER_API: currentConfig.BUZZER_API,
  SHOPIFY_WEBVIEW_URL: currentConfig.SHOPIFY_WEBVIEW_URL,
  SHOPIFY_WEBVIEW_ENTRY_CACHE:
    currentConfig.SHOPIFY_WEBVIEW_ENTRY_CACHE !== false,
  // Shopify Storefront GraphQL endpoint (tokenless). The host is the
  // merchant's own store domain (from the Shopify integration), so only the
  // versioned path is env-independent config.
  SHOPIFY_STOREFRONT_API_PATH: '/api/2026-04/graphql.json',
  // Shopify admin host. Env-independent: the same host serves every merchant,
  // and the store is addressed by handle in the path.
  SHOPIFY_ADMIN_URL: 'https://admin.shopify.com',
  // "What is this?" guide linked from the expired-24h-window support strip in
  // chat. Env-independent marketing-site lesson.
  CHAT_SUPPORT_WINDOW_GUIDE_URL:
    'https://www.zoko.io/learning-micro-lessons/24-hours-window-in-chat',
  // "Learn more" on the bubble shown when a customer's media exceeded Meta's
  // 100 MB limit and never reached Zoko (ZC-3476). Env-independent article.
  CHAT_MEDIA_SIZE_GUIDE_URL: 'https://www.zoko.io/learning-article/media-sizes',
}

export default config
